import { useQuery } from '@tanstack/react-query'
import { postApi } from '../../services/api'
import { Alert } from '../common'
import { flaggedCategories, moderationStatusView } from '../moderation/moderationReviewModel'
import { moderationPollInterval } from '../moderation/moderationHoldStatus'
import { uniqueFindingLabels } from '../moderation/moderationFindings'
import { HighlightedText } from '../moderation/HighlightedText'
import styles from './ModerationStatusPanel.module.css'

const TONE_VARIANTS = { success: 'success', warning: 'warning', error: 'error', info: 'info', default: 'info' }

// FindingList puts each finding into words once. texts, when given, is the
// submitted field text the findings' offsets count into, so a phrase finding
// can quote the editor's own words.
function FindingList({ findings = [], texts }) {
  const labels = uniqueFindingLabels(findings, texts)
  if (!labels.length) return null
  return (
    <ul className={styles.findings} aria-label="Moderation findings">
      {labels.map((label) => <li key={label}>{label}</li>)}
    </ul>
  )
}

const HIGHLIGHT_CLASSES = { root: styles.highlighted, label: styles.fieldLabel, mark: styles.mark }

const BLOCK_FIELDS = [
  { field: 'text', label: 'Text' },
  { field: 'title', label: 'Title' },
  { field: 'subtitle', label: 'Subtitle' },
  { field: 'cta_label', label: 'Call to action' },
]

// What the editor changes to get past a refusal, by the field it names.
const REFUSAL_HINTS = {
  cta_target: 'Your draft is kept. Change the link and save again.',
  caption_label: 'Your draft is kept. Change the caption label and save again.',
}

function refusalHint(block) {
  if (block?.code === 'moderation_asset_rejected') return 'Your draft is kept. Remove the rejected media and save again.'
  return REFUSAL_HINTS[block?.field] || 'Your draft is kept. Change the content and save again.'
}

// rejectedMediaLine names the composer's media a refusal says moderation
// rejected; media the composer does not show (a poster, an item removed
// since) is only counted. '' when the refusal names none.
function rejectedMediaLine(block, media) {
  const rejected = new Set(block?.rejectedAssets || [])
  if (!rejected.size) return ''
  const names = (media || [])
    .filter((item) => item?.mediaId && rejected.has(item.mediaId))
    .map((item) => item.name || item.title)
    .filter(Boolean)
  if (names.length) return `Rejected media: ${names.join(', ')}.`
  return `Rejected media: ${rejected.size} ${rejected.size === 1 ? 'item' : 'items'}.`
}

// refusalIsStale reports whether the editor changed any field the refusal
// was computed against: its highlights no longer point at the live text.
function refusalIsStale(block, values) {
  if (!block?.values) return false
  return Object.entries(block.values).some(([field, refused]) => (refused || '') !== (values?.[field] || ''))
}

// ModerationStatusPanel is the composer's inline moderation feedback: the
// refusal of the last save in enforce mode, and, for a saved post, its
// current moderation state, polled until its checks finish. It has no
// decision controls; brand admins decide in the moderation queue.
// A refusal carries the field values it was computed against (block.values);
// values, the live form, is only a fallback, because offsets count into the
// submitted text. Once the editor changes one of those fields the refusal
// stops marking the old text and says the new text is checked on save.
// media, the composer's attachments, names the media a refusal rejected.
// showUnenforced=false leaves out the "not enforced" notice, which only says
// which switch keeps moderation from enforcing the post; Wager staff still
// see it.
export function ModerationStatusPanel({ orgSlug, postId, block, values = {}, media = [], showUnenforced = true }) {
  const postQuery = useQuery({
    queryKey: ['post-moderation', orgSlug, postId],
    queryFn: () => postApi.get(orgSlug, postId),
    enabled: Boolean(orgSlug && postId),
    refetchInterval: (query) => moderationPollInterval(query.state.data?.moderation),
  })
  const moderation = postQuery.data?.moderation
  const statusView = moderationStatusView(moderation)
  const view = statusView?.unenforced && !showUnenforced ? null : statusView

  if (!block && !view) return null
  const blockValues = block?.values || values
  const stale = refusalIsStale(block, values)
  const rejectedMedia = block?.code === 'moderation_asset_rejected' ? rejectedMediaLine(block, media) : ''
  return (
    <section className={styles.panel} aria-label="Content moderation" data-testid="moderation-status-panel">
      {block ? (
        <Alert variant="error">
          <strong>Not saved</strong>
          <p>{block.message}</p>
          {block.code === 'moderation_phrase_blocked' && !stale
            ? BLOCK_FIELDS.map(({ field, label }) => (
              <HighlightedText key={field} label={label} field={field} text={blockValues[field] || ''} findings={block.findings} classes={HIGHLIGHT_CLASSES} />
            ))
            : null}
          {block.code === 'moderation_scope_unresolved' && block.unknownTargets.length
            ? <p>Unknown targets: {block.unknownTargets.join(', ')}</p>
            : null}
          {rejectedMedia ? <p>{rejectedMedia}</p> : null}
          <FindingList findings={block.findings} texts={blockValues} />
          {stale
            ? <p className={styles.stale}>You changed the post since this check. Save to check the new text.</p>
            : <p className={styles.hint}>{refusalHint(block)}</p>}
        </Alert>
      ) : null}
      {view && !block ? (
        <Alert variant={TONE_VARIANTS[view.tone] || 'info'}>
          <strong>{view.title}</strong>
          <p>{view.description}</p>
          {view.reason ? <p>Reason: {view.reason}</p> : null}
          {view.findings === 'reviewed'
            ? (moderation?.findings?.length
              ? <p className={styles.hint}>Flagged for: {flaggedCategories(moderation.findings)}</p>
              : null)
            : <FindingList findings={moderation?.findings || []} />}
          {moderation?.findings_hidden ? <p className={styles.hint}>Findings are visible to brand admins and the post&apos;s author.</p> : null}
        </Alert>
      ) : null}
    </section>
  )
}

export default ModerationStatusPanel
